import React, { createContext, useState, useEffect, useMemo, useContext } from 'react';
import Mesh from '../mesh';
import { mscore, WeightedScores } from '../utils';
import UserSelectionContext from './UserSelectionProvider';
import { getParams, getRunIds, getScores } from '../services/runs';
import { Run } from '../types';

// TODO refactor types
interface DataContextType {
    scores: any;
    weights: any;
    meshTree: any;
    rankings: any;
    histograms: any;
    scoresDomain: number[];
    loadingMeshTree: boolean;
    loadingRankings: boolean;
    availableRuns: Run[];
    selectedRun: Run;
    maxWeights: number;
    meshName2Ids: { [key: string]: string[] }
    meshId2Name: { [key: string]: string }
    setLoadingRankings: (loading: boolean) => void
    setLoadingMeshTree: (loading: boolean) => void
    changeSelectedRun: (run: Run) => void
    updateWeights: (runId, key, nkey, newWeights) => void
    cloneWeights: (runId, key) => void
    removeWeights: (runId, key) => void
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [weights, setweights] = useState<any>(null);
    const [scores, setScores] = useState<any>(null);
    // const [sankeyGraph, setSankeyGraph] = useState<any>(null);
    const { streamsEnabled, selectedMoa, selectedWeights, setSelectedWeights } = useContext(UserSelectionContext)
    const [availableRuns, setAvailableRuns] = useState<Run[]>([]);
    const [selectedRun, setSelectedRun] = useState<Run>(null);
    const [loadingMeshTree, setLoadingMeshTree] = useState<boolean>(true);
    const [loadingRankings, setLoadingRankings] = useState<boolean>(true);
    const maxWeights = 5
    let [weightId, setWeightId] = useState<string>('B');

    // useEffect(() => {
    //     if (!weights || !selectedWeights || !streamsEnabled || !selectedRun || !selectedMoa) return


    //     // console.log('selectedWeights', weights[selectedRun.run_id][selectedWeights])
    //     // console.log('alpha', alpha)

    //     const callGetSankey = async () => {
    //         const sankeyData = await getEvidenceSummary(selectedRun.run_id,  selectedMoa, "Pneumonia")
    //         // console.log(sankeyData)
    //         // const alpha = weights[selectedRun.run_id].alpha
    //         // const graph = new SankeyGraph(sankeyData, weights[selectedRun.run_id][selectedWeights], alpha)
    //         // setSankeyGraph(graph.graph)
    //         setSankeyGraph(sankeyData)
    //     }
    //     callGetSankey()

    // }, [weights, selectedWeights, streamsEnabled, selectedMoa, selectedRun])

    useEffect(() => {
        setLoadingMeshTree(true)
    }, [selectedMoa])

    useEffect(() => {
        setLoadingRankings(true)
    }, [selectedMoa])

    useEffect(() => {
        if (!selectedMoa) return
        const callGetRuns = async () => {
            const runs = await getRunIds(selectedMoa)
            setAvailableRuns(runs.map(r => ({ ...r, date: new Date(r.date) })).sort((a, b) => b.date.getTime() - a.date.getTime()));
        }
        callGetRuns()
    }, [selectedMoa])

    useEffect(() => {
        if (!availableRuns.length) return
        setSelectedRun(availableRuns[0])
    }, [availableRuns])


    // useEffect(() => {
    //     if (!weights || !selectedRun) return
    //     const weightIds = Object.keys(weights[selectedRun.run_id]).map(k => Number(k)).filter(k => !isNaN(k))
    //     if (!weightIds.includes(weightId)) return
    //     const validIds = Array.from({ length: maxWeights - 1 }, (_, i) => i + 1);
    //     const missingIds = validIds.filter(value => !weightIds.includes(value));
    //     setWeightId(missingIds.length ? Math.min(...missingIds) : 0)
    // }, [weights])

    useEffect(() => {
        if (!weights || !selectedRun) return;

        const weightIds = Object.keys(weights[selectedRun.run_id]).filter(k => ['A', 'B', 'C', 'D', 'E'].includes(k))

        if (!weightIds.includes(weightId)) return;

        const validIds = ['A', 'B', 'C', 'D', 'E'];
        const missingIds = validIds.filter(value => !weightIds.includes(value));

        setWeightId(missingIds.length ? missingIds[0] : 'A');
    }, [weights]);


    // useEffect(() => {
    //     setLoadingMeshTree(true)
    // }, [selectedRun])

    // useEffect(() => {
    //     setLoadingRankings(true)
    // }, [selectedRun])

    // useEffect(() => {
    //     setSelectedWeights('weights')
    // }, [selectedRun])

    useEffect(() => {
        if (!selectedMoa || !selectedRun) return // refactor: handle unsetting data / moa
        const callGetData = async () => {
            const scores = await getScores(selectedRun.run_id, selectedMoa)
            const weights = await getParams(selectedRun.run_id, selectedMoa)
            setScores(scores)
            setweights(weights)
        }
        callGetData()
    }, [selectedRun, selectedMoa])

    const changeSelectedRun = (run: Run) => {
        setLoadingMeshTree(true)
        setLoadingRankings(true)
        setSelectedWeights('A')
        setSelectedRun(run)
    }

    const updateWeights = (runId, wkey, nwkey, newWeights) => {
        setweights(prevWeights => {
            const updatedWeights = { ...prevWeights };

            if (nwkey !== wkey) {
                const { [wkey]: removed, ...remainingRunWeights } = updatedWeights[runId] || {};
                updatedWeights[runId] = remainingRunWeights;
            }

            updatedWeights[runId] = {
                ...updatedWeights[runId],
                [nwkey]: newWeights
            };

            return updatedWeights;
        });
    }

    const removeWeights = (runId, wkey) => {
        if (wkey === selectedWeights) {
            setSelectedWeights('A')
        }
        setweights(prevWeights => {
            const { [wkey]: _, ...restWeights } = prevWeights[runId];
            return {
                ...prevWeights,
                [runId]: restWeights
            };
        })
    }

    const cloneWeights = (runId, wkey) => {
        setweights(prevWeights => ({
            ...prevWeights,
            [runId]: {
                ...prevWeights[runId],
                [weightId]: prevWeights[runId][wkey]
            }
        }))
    }

    const rankings = useMemo(() => {
        setLoadingRankings(true)

        if (!scores || !weights || !weights.hasOwnProperty(selectedRun.run_id)) return []

        const newVal = Object.entries(scores).map(([k, v]) => ({
            name: k,
            mscore: Object.fromEntries(
                Object.keys(weights[selectedRun.run_id]).filter(k => !k.startsWith('alpha')).map(wkey => [
                    wkey,
                    { 'mscore': mscore(v[selectedRun.run_id], weights[selectedRun.run_id][wkey], streamsEnabled) }
                ])
            ),
            detailed_score: Object.fromEntries(
                Object.keys(weights[selectedRun.run_id]).filter(k => !k.startsWith('alpha')).map(wkey => [
                    wkey,
                    WeightedScores(v[selectedRun.run_id], weights[selectedRun.run_id][wkey], streamsEnabled)
                ])
            )
        }));

        // Adding rank for each wkey based on mscore
        Object.keys(weights[selectedRun.run_id]).filter(k => !k.startsWith('alpha')).forEach(wkey => {
            const scoresForWkey = newVal.map(entry => ({
                name: entry.name,
                mscore: entry.mscore[wkey].mscore
            }));
            let prevEntry = null
            scoresForWkey.sort((a, b) => {
                return b.mscore !== a.mscore ? b.mscore - a.mscore : a.name.localeCompare(b.name);
            }).forEach((entry, index) => {
                const originalEntry = newVal.find(e => e.name === entry.name)
                if (!originalEntry.hasOwnProperty('rank'))
                    originalEntry['rank'] = {}
                if (prevEntry !== null && Math.round(prevEntry.mscore * 100) === Math.round(entry.mscore * 100))
                    originalEntry['rank'] = { ...originalEntry['rank'], [wkey]: newVal.find(e => e.name === prevEntry.name)['rank'][wkey] };
                else
                    if (prevEntry !== null)
                        originalEntry['rank'] = { ...originalEntry['rank'], [wkey]: { 'rank': newVal.find(e => e.name === prevEntry.name)['rank'][wkey]['rank'] + 1 } };
                    else originalEntry['rank'] = { ...originalEntry['rank'], [wkey]: { 'rank': index + 1 } };
                prevEntry = entry
            });
        });

        setLoadingRankings(false)

        return newVal
    }, [scores, weights, streamsEnabled])

    const histograms = useMemo(() => {
        let histograms = {
            mscore: {},
            ...Object.fromEntries(Object.entries(streamsEnabled).filter(([k, v]) => v).map(([k, v]) => [k, {}]))
        }
        rankings.forEach(ind => {
            Object.entries(ind.mscore).forEach(([wk, score]) => {
                if (score.mscore !== null) {
                    if (!(wk in histograms.mscore)) histograms.mscore[wk] = []
                    histograms.mscore[wk].push(score.mscore * 100)
                }
            })

            Object.entries(ind.detailed_score).forEach(([wk, scores]) => {
                Object.entries(scores).forEach(([scoreType, score]) => {
                    if (score !== null) {
                        if (!(wk in histograms[scoreType])) histograms[scoreType][wk] = []
                        histograms[scoreType][wk].push(score * 100)
                    }
                })
            })
        })
        return histograms
    }, [rankings])

    const meshTree = useMemo(() => {
        setLoadingMeshTree(true)

        if (!scores || !weights || !weights.hasOwnProperty(selectedRun.run_id)) return []

        const newVal = [...Mesh.getTreesWithScores(scores, weights, selectedRun.run_id, selectedWeights, streamsEnabled)]

        setLoadingMeshTree(false)

        return newVal
    }, [scores, weights, selectedWeights, streamsEnabled])

    const meshName2Ids = useMemo(() => {
        return require('../assets/data/mesh_name2ids.json')
    }, [])

    const meshId2Name = useMemo(() => {
        return require('../assets/data/mesh_id2name.json')
    }, [])

    const scoresDomain = useMemo(() => {
        const flatScores = Object.entries(rankings).map(([_, scores]) => [
            ...Object.values(scores.detailed_score).map(s => Object.values(s)),
            ...Object.values(scores.mscore).map(s => Object.values(s))
        ].flat()).flat();
        return [Math.min(...flatScores), 1]
    }, [rankings])

    return (
        <DataContext.Provider value={{
            scores, weights, meshTree, rankings, scoresDomain, histograms,
            loadingMeshTree, loadingRankings, selectedRun, availableRuns, maxWeights, meshName2Ids, meshId2Name,
            setLoadingRankings, setLoadingMeshTree, changeSelectedRun, updateWeights, cloneWeights, removeWeights
        }}>
            {children}
        </DataContext.Provider>
    );
}

export default DataContext;
